import { useNuxtApp } from '#app';
import { onBeforeUnmount, onMounted } from 'vue';

import { PerformanceMetricsService } from '~/services/PerformanceMetricsService';
import { createLogger } from '~/utils/logger';

const logger = createLogger('performance-reporting');

/**
 * Performance reporting composable
 * Automatically initializes performance metrics reporting for Three.js scenes
 */
export const usePerformanceReporting = (performanceMetrics) => {
    const { $datadogRum } = useNuxtApp();
    const performanceService = new PerformanceMetricsService();

    /**
     * Start performance metrics reporting
     */
    const startReporting = () => {
        if (!performanceMetrics) {
            logger.warn('Performance metrics unavailable', {
                event: 'performance.metrics_unavailable',
                reason: 'metrics_missing',
            });
            return;
        }

        if (!$datadogRum) {
            logger.warn('Performance reporting backend unavailable', {
                event: 'performance.reporting_backend_unavailable',
                backend: 'datadog_rum',
            });
            return;
        }

        try {
            performanceService.initialize(performanceMetrics, $datadogRum);
        } catch (error) {
            logger.error('Failed to start performance metrics reporting', error);
        }
    };

    /**
     * Stop performance metrics reporting
     */
    const stopReporting = () => {
        try {
            performanceService.stop();
        } catch (error) {
            logger.error('Failed to stop performance metrics reporting', error);
        }
    };

    /**
     * Send immediate performance report
     */
    const sendImmediateReport = () => {
        if (!performanceService.isActive) {
            logger.warn('Performance reporting is not active', {
                event: 'performance.reporting_inactive',
                action: 'send_immediate_report',
            });
            return;
        }

        try {
            performanceService.sendImmediateReport();
        } catch (error) {
            logger.error('Failed to send immediate performance report', error);
        }
    };

    /**
     * Update reporting interval
     * @param {number} interval - New interval in milliseconds
     */
    const updateReportingInterval = (interval) => {
        if (!performanceService.isActive) {
            logger.warn('Performance reporting is not active', {
                event: 'performance.reporting_inactive',
                action: 'update_interval',
                interval,
            });
            return;
        }

        try {
            performanceService.updateReportingInterval(interval);
        } catch (error) {
            logger.error('Failed to update reporting interval', { interval }, error);
        }
    };

    /**
     * Get current performance thresholds
     */
    const getThresholds = () => {
        return performanceService.getThresholds();
    };

    // Auto-start reporting when component mounts
    onMounted(() => {
        // Small delay to ensure Three.js scene is initialized
        setTimeout(startReporting, 100);
    });

    // Auto-stop reporting when component unmounts
    onBeforeUnmount(() => {
        stopReporting();
    });

    return {
        startReporting,
        stopReporting,
        sendImmediateReport,
        updateReportingInterval,
        getThresholds,

        // Reactive state
        get isReporting() {
            return performanceService.isActive;
        },
    };
};
